import { useEffect, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { getCurrentSeason } from '../../../data/seasons';
import { listSeasons } from '../../../utils/seasonApi';
import {
  RECENT_CACHE_TTL_MS,
  dedupeByKey,
  formatDateForRecent,
  getSortTimestamp,
  sortRecentItems,
} from '../utils/recentHelpers';
import {
  getCachedPlayerSeasonEntries,
  getCachedRecentSubmissions,
  setCachedPlayerSeasonEntries,
  setCachedRecentSubmissions,
  clearPlayerStatsSeasonCache,
} from '../../../utils/playerStatsSessionCache';

const initialSections = {
  team: [],
  enemy: [],
  player: [],
};

const getYearFromDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return String(parsed.getFullYear());
};

const getSeasonIdFromPath = (path) => {
  const match = String(path || '').match(/\/seasons\/([^/]+)\//);
  return match ? match[1] : null;
};

const pickMapValue = (obj = {}) =>
  obj.Map ||
  obj.map ||
  obj.selectedMap ||
  obj.mapName ||
  obj.MapName ||
  '';

const pickFirst = (obj = {}, keys = []) => {
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const normalizeMatchPayload = (payload = {}) => ({
  mode: pickFirst(payload, ['Mode', 'mode']),
  map: pickMapValue(payload),
  opponent: pickFirst(payload, ['EnemyTeam', 'VS', 'OpponentTeam', 'opponent', 'vs']),
  opponentRegion: pickFirst(payload, ['EnemyRegion', 'Region', 'OpponentRegion', 'opponentRegion']),
  matchCategory: pickFirst(payload, ['MatchCategory', 'matchCategory']),
  competitionRegion: pickFirst(payload, ['MatchTypeRegion', 'competitionRegion']),
  competition: pickFirst(payload, ['MatchType', 'Competition', 'matchType']),
  result: pickFirst(payload, ['WinLose', 'Result', 'result']),
  scoreFor: pickFirst(payload, ['PF', 'PointsFor', 'scoreFor']),
  scoreAgainst: pickFirst(payload, ['PA', 'PointsAgainst', 'scoreAgainst']),
  notes: pickFirst(payload, ['Notes', 'notes']),
  source: pickFirst(payload, ['source']),
  mirrorId: pickFirst(payload, ['mirrorId']),
  sourceModeId: pickFirst(payload, ['sourceModeId']),
  sourceSeasonId: pickFirst(payload, ['sourceSeasonId']),
  sourceCollection: pickFirst(payload, ['sourceCollection']),
  reciprocalModeId: pickFirst(payload, ['ReciprocalEnemyModeID']),
  reciprocalSeasonId: pickFirst(payload, ['ReciprocalEnemySeasonID']),
});

const normalizePlayerEntry = (payload = {}) => ({
  player: pickFirst(payload, ['Player', 'player']),
  operator: pickFirst(payload, ['Operator', 'operator']),
  score: pickFirst(payload, ['Score', 'score']),
  K: pickFirst(payload, ['K']),
  D: pickFirst(payload, ['D']),
  A: pickFirst(payload, ['A']),
  HillTime: pickFirst(payload, ['HillTime']),
  Impact: pickFirst(payload, ['Impact']),
  TotalDamage: pickFirst(payload, ['TotalDamage']),
  CaptureKills: pickFirst(payload, ['CaptureKills']),
  DistanceMoved: pickFirst(payload, ['DistanceMoved']),
  OperatorValue: pickFirst(payload, ['OperatorValue']),
  ADR: pickFirst(payload, ['ADR']),
  FirstBloods: pickFirst(payload, ['FirstBloods']),
  Clutches1v1: pickFirst(payload, ['Clutches1v1']),
  Damage: pickFirst(payload, ['Damage']),
});

const buildSeasonCandidates = (rows, seasonToken) => {
  const token = String(seasonToken || '').trim();
  if (!token) return [];

  const matches = (rows || []).filter((row) => {
    const id = String(row?.id || '').trim();
    const year = String(row?.year || '').trim();
    return id === token || year === token;
  });

  const candidates = [
    token,
    ...matches.flatMap((row) => [String(row?.id || '').trim(), String(row?.year || '').trim()]),
  ].filter(Boolean);

  return [...new Set(candidates)];
};

export const useRecentSubmissions = ({ activeTab, teamId, uid, selectedSeason }) => {
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentSubmissions, setRecentSubmissions] = useState(initialSections);
  const [availableSeasons, setAvailableSeasons] = useState([]);

  const seasonsCacheRef = useRef(new Map());

  useEffect(() => {
    if (activeTab !== 'recentSubmissions' || !teamId || !uid) return;

    let active = true;

    const normalizeItem = (item) => ({
      ...item,
      date: formatDateForRecent(item.rawDate),
      sortTs: getSortTimestamp(item.rawDate),
    });

    const safeFetch = async (colPath, mapper) => {
      try {
        const colRef = collection(db, colPath);
        const snap = await getDocs(colRef);
        return snap.docs.map(mapper);
      } catch (err) {
        console.warn('Recent fetch failed for', colPath, err);
        return [];
      }
    };

    const safeFetchPlayerMatchesFromFlatEntries = async (entriesPath, seasonKey) => {
      try {
        let flatEntries = getCachedPlayerSeasonEntries(teamId, seasonKey, RECENT_CACHE_TTL_MS);
        if (!Array.isArray(flatEntries)) {
          const entriesRef = collection(db, entriesPath);
          const entriesSnap = await getDocs(entriesRef);
          flatEntries = entriesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
          setCachedPlayerSeasonEntries(teamId, seasonKey, flatEntries);
        }
        if (!flatEntries.length) return [];

        const grouped = new Map();

        flatEntries.forEach((payload) => {
          const matchId = String(payload.matchId || payload.matchRef || payload.ModeID || '').trim();
          if (!matchId) return;
          const groupKey = String(payload.matchId || payload.matchRef || `${seasonKey}:${payload.ModeID || matchId}`).trim();
          const meta = normalizeMatchPayload(payload);

          const existing = grouped.get(groupKey) || {
            id: matchId,
            matchId,
            seasonId: seasonKey,
            type: 'Player Match',
            rawDate: payload.Date || '',
            mode: meta.mode,
            vs: meta.opponent,
            opponent: meta.opponent,
            opponentRegion: meta.opponentRegion,
            map: meta.map,
            matchCategory: meta.matchCategory,
            competitionRegion: meta.competitionRegion,
            competition: meta.competition,
            result: meta.result,
            scoreFor: meta.scoreFor,
            scoreAgainst: meta.scoreAgainst,
            players: [],
            fallbackPlayer: payload.Player || '',
          };

          const playerName = String(payload.Player || '').trim();
          if (playerName) {
            existing.players.push(playerName);
          }
          existing.playerDetails = [
            ...(existing.playerDetails || []),
            normalizePlayerEntry(payload),
          ];

          if (!existing.rawDate && payload.Date) existing.rawDate = payload.Date;
          if (!existing.mode && meta.mode) existing.mode = meta.mode;
          if (!existing.vs && meta.opponent) existing.vs = meta.opponent;
          if (!existing.opponent && meta.opponent) existing.opponent = meta.opponent;
          if (!existing.opponentRegion && meta.opponentRegion) existing.opponentRegion = meta.opponentRegion;
          if (!existing.map) existing.map = meta.map;
          if (!existing.matchCategory && meta.matchCategory) existing.matchCategory = meta.matchCategory;
          if (!existing.competitionRegion && meta.competitionRegion) existing.competitionRegion = meta.competitionRegion;
          if (!existing.competition && meta.competition) existing.competition = meta.competition;
          if (!existing.result && meta.result) existing.result = meta.result;
          if (existing.scoreFor === '' && meta.scoreFor !== '') existing.scoreFor = meta.scoreFor;
          if (existing.scoreAgainst === '' && meta.scoreAgainst !== '') existing.scoreAgainst = meta.scoreAgainst;
          if (!existing.fallbackPlayer && payload.Player) existing.fallbackPlayer = payload.Player;

          grouped.set(groupKey, existing);
        });

        return Array.from(grouped.values()).map((item) => {
          const uniquePlayers = [...new Set(item.players.filter(Boolean))];
          const playersLabel = uniquePlayers.join(', ');
          return normalizeItem({
            id: item.id,
            matchId: item.matchId,
            seasonId: item.seasonId,
            type: item.type,
            rawDate: item.rawDate,
            mode: item.mode,
            vs: item.vs,
            opponent: item.opponent || item.vs,
            opponentRegion: item.opponentRegion,
            map: item.map,
            matchCategory: item.matchCategory,
            competitionRegion: item.competitionRegion,
            competition: item.competition,
            result: item.result,
            scoreFor: item.scoreFor,
            scoreAgainst: item.scoreAgainst,
            player: playersLabel || item.fallbackPlayer || '',
            players: item.playerDetails || [],
            deleteRefPath: seasonKey
              ? `teams/${teamId}/seasons/${seasonKey}/database/playerStats/matches/${item.matchId}`
              : `teams/${teamId}/database/playerStats/matches/${item.matchId}`,
            path: seasonKey
              ? `/addPlayerStatsForm?teamId=${teamId}&seasonId=${seasonKey}&matchId=${item.matchId}`
              : `/addPlayerStatsForm?teamId=${teamId}&matchId=${item.matchId}`,
          });
        });
      } catch (err) {
        console.warn('Recent flat player entries fetch failed for', entriesPath, err);
        return [];
      }
    };

    const getSeasonsForTeam = async () => {
      const cached = seasonsCacheRef.current.get(teamId);
      if (cached) return cached;

      try {
        const rows = await listSeasons(teamId);
        seasonsCacheRef.current.set(teamId, rows);
        return rows;
      } catch (err) {
        console.warn('Recent listSeasons failed for', teamId, err);
        return [];
      }
    };

    const fetchRecent = async () => {
      const currentYear = String(getCurrentSeason());

      setRecentLoading(true);

      try {
        const seasons = await getSeasonsForTeam();
        const seasonDocForYear = seasons.find((s) => String(s.year || s.id) === currentYear);
        const resolvedSeasonId = seasonDocForYear ? String(seasonDocForYear.id) : currentYear;
        const selectedSeasonId = String(selectedSeason || resolvedSeasonId);

        const seasonOptions = dedupeByKey(
          (seasons || [])
            .map((s) => ({
              value: String(s.id || '').trim(),
              label: String(s.year || s.id || '').trim(),
            }))
            .filter((s) => s.value && s.label),
          (s) => s.value
        ).sort((a, b) => String(b.label).localeCompare(String(a.label)));
        const mergedSeasonOptions = dedupeByKey(
          [{ value: selectedSeasonId, label: selectedSeasonId }, ...seasonOptions],
          (s) => s.value
        );
        if (active) setAvailableSeasons(mergedSeasonOptions);

        const cachedRecent = getCachedRecentSubmissions(teamId, selectedSeasonId, RECENT_CACHE_TTL_MS);
        if (cachedRecent) {
          if (!active) return;
          setRecentSubmissions(cachedRecent);
          setRecentLoading(false);
          return;
        }

        const seasonIds = buildSeasonCandidates(seasons, selectedSeasonId);

        const teamSeasonPromises = seasonIds.map((seasonKey) =>
          safeFetch(`teams/${teamId}/seasons/${seasonKey}/database/teamStats/modes`, (d) => {
            const payload = d.data() || {};
            const meta = normalizeMatchPayload(payload);
            return normalizeItem({
              id: d.id,
              seasonId: seasonKey,
              type: 'Team Summary',
              rawDate: payload.Date || '',
              mode: meta.mode,
              vs: meta.opponent,
              opponent: meta.opponent,
              opponentRegion: meta.opponentRegion,
              map: meta.map,
              matchCategory: meta.matchCategory,
              competitionRegion: meta.competitionRegion,
              competition: meta.competition,
              result: meta.result,
              scoreFor: meta.scoreFor,
              scoreAgainst: meta.scoreAgainst,
              notes: meta.notes,
              reciprocalModeId: meta.reciprocalModeId,
              reciprocalSeasonId: meta.reciprocalSeasonId,
              sourceData: payload,
              deleteRefPath: `teams/${teamId}/seasons/${seasonKey}/database/teamStats/modes/${d.id}`,
              path: `/submit-team-summary?teamId=${teamId}&seasonId=${seasonKey}&modeId=${d.id}`,
            });
          })
        );

        const enemySeasonPromises = seasonIds.map((seasonKey) =>
          safeFetch(`teams/${teamId}/seasons/${seasonKey}/database/enemyStats/modes`, (d) => {
            const payload = d.data() || {};
            const meta = normalizeMatchPayload(payload);
            return normalizeItem({
              id: d.id,
              seasonId: seasonKey,
              type: 'Enemy Scouting',
              rawDate: payload.Date || '',
              mode: meta.mode,
              team: payload.Team || '',
              teamRegion: payload.TeamRegion || '',
              vs: meta.opponent,
              opponent: meta.opponent,
              opponentRegion: meta.opponentRegion,
              map: meta.map,
              matchCategory: meta.matchCategory,
              competitionRegion: meta.competitionRegion,
              competition: meta.competition,
              result: meta.result,
              scoreFor: meta.scoreFor,
              scoreAgainst: meta.scoreAgainst,
              notes: meta.notes,
              source: meta.source,
              mirrorId: meta.mirrorId,
              sourceModeId: meta.sourceModeId,
              sourceSeasonId: meta.sourceSeasonId,
              sourceCollection: meta.sourceCollection,
              sourceData: payload,
              deleteRefPath: `teams/${teamId}/seasons/${seasonKey}/database/enemyStats/modes/${d.id}`,
              path: `/submit-enemy-stats?teamId=${teamId}&seasonId=${seasonKey}&modeId=${d.id}`,
            });
          })
        );
        const playerSeasonPromises = seasonIds.map((seasonKey) =>
          safeFetchPlayerMatchesFromFlatEntries(
            `teams/${teamId}/seasons/${seasonKey}/database/playerStats/entries`,
            seasonKey
          )
        );

        const [teamSeasonBuckets, enemySeasonBuckets, playerSeasonBuckets] = await Promise.all([
          Promise.all(teamSeasonPromises),
          Promise.all(enemySeasonPromises),
          Promise.all(playerSeasonPromises),
        ]);

        const teamSeasonRows = (teamSeasonBuckets || []).flat();
        const enemySeasonRows = (enemySeasonBuckets || []).flat();
        const playerSeasonMatches = dedupeByKey(
          (playerSeasonBuckets || []).flat(),
          (item) => `${item.seasonId || 'legacy'}:${item.matchId || item.id}`
        );

        const playerRows = [...playerSeasonMatches];

        const teamRows = sortRecentItems(
          dedupeByKey(teamSeasonRows, (item) =>
            `${item.seasonId || 'legacy'}:${item.id}`
          )
        );

        const enemyRows = sortRecentItems(
          dedupeByKey(enemySeasonRows, (item) =>
            `${item.seasonId || 'legacy'}:${item.id}`
          )
        );

        const playerFinalRows = sortRecentItems(
          dedupeByKey(playerRows, (item) =>
            `${item.seasonId || 'legacy'}:${item.matchId ? `match:${item.matchId}` : `entry:${item.id}`}`
          )
        );

        const payload = {
          team: teamRows,
          enemy: enemyRows,
          player: playerFinalRows,
        };

        if (process.env.NODE_ENV !== 'production') {
          const modeCounts = playerFinalRows.reduce((acc, row) => {
            const mode = String(row.mode || '').toUpperCase();
            if (mode.includes('HP') || mode.includes('HARDPOINT')) acc.HP += 1;
            else if (mode.includes('SND') || mode.includes('SEARCH')) acc.SND += 1;
            else if (mode.includes('CTL') || mode.includes('CTRL') || mode.includes('CONTROL')) acc.CTL += 1;
            else acc.UNKNOWN += 1;
            return acc;
          }, { HP: 0, SND: 0, CTL: 0, UNKNOWN: 0 });
          console.debug('[RecentSubmissions] player mode counts', modeCounts, 'total', playerFinalRows.length);
        }

        if (!active) return;
        setCachedRecentSubmissions(teamId, selectedSeasonId, payload);
        setRecentSubmissions(payload);
      } finally {
        if (active) setRecentLoading(false);
      }
    };

    fetchRecent().catch((err) => {
      console.error('Failed to fetch recent submissions:', err);
      if (active) setRecentLoading(false);
    });

    return () => {
      active = false;
    };
  }, [activeTab, selectedSeason, teamId, uid]);

  const deleteRecentSubmission = async (item) => {
    const deleteRefPath = item?.deleteRefPath;
    if (!teamId || !deleteRefPath) {
      return { ok: false, error: new Error('Missing delete reference') };
    }

    try {
      const refsToDelete = new Set([deleteRefPath]);
      const baseRef = doc(db, deleteRefPath);
      const baseSnap = await getDoc(baseRef);
      const baseData = baseSnap.exists() ? (baseSnap.data() || {}) : {};

      // Team Summary -> reciprocal enemy scouting
      if (item?.type === 'Team Summary' || item?.type === 'Team Match') {
        const reciprocalModeId = String(baseData.ReciprocalEnemyModeID || '').trim();
        const reciprocalSeasonId = String(baseData.ReciprocalEnemySeasonID || '').trim();
        if (reciprocalModeId && reciprocalSeasonId) {
          refsToDelete.add(`teams/${teamId}/seasons/${reciprocalSeasonId}/database/enemyStats/modes/${reciprocalModeId}`);
        }
      }

      // Enemy Scouting mirror pair -> delete mirror too
      if (item?.type === 'Enemy Scouting') {
        const mirrorId = String(baseData.mirrorId || '').trim();
        const seasonCandidates = [
          item?.seasonId,
          baseData.seasonId,
          baseData.SeasonId,
          getYearFromDate(baseData.Date),
        ]
          .map((value) => String(value || '').trim())
          .filter(Boolean);
        if (mirrorId) {
          for (const seasonKey of [...new Set(seasonCandidates)]) {
            refsToDelete.add(`teams/${teamId}/seasons/${seasonKey}/database/enemyStats/modes/${mirrorId}`);
          }
        }

        // Reciprocal enemy created from Team Summary -> delete source team summary too
        const sourceModeId = String(baseData.sourceModeId || '').trim();
        const sourceSeasonId = String(baseData.sourceSeasonId || '').trim();
        const isTeamSummarySource =
          baseData.source === 'teamSummaryReciprocal' &&
          baseData.sourceCollection === 'teamStats' &&
          !!sourceModeId;

        if (isTeamSummarySource) {
          const teamSeasonCandidates = [
            sourceSeasonId,
            item?.seasonId,
            baseData.SeasonId,
            getYearFromDate(baseData.Date),
          ]
            .map((value) => String(value || '').trim())
            .filter(Boolean);

          let linkedFound = false;
          for (const seasonKey of [...new Set(teamSeasonCandidates)]) {
            const linkedPath = `teams/${teamId}/seasons/${seasonKey}/database/teamStats/modes/${sourceModeId}`;
            const linkedSnap = await getDoc(doc(db, linkedPath));
            if (linkedSnap.exists()) {
              refsToDelete.add(linkedPath);
              linkedFound = true;
              break;
            }
          }

          if (!linkedFound) {
            const legacyPath = `teams/${teamId}/database/teamStats/modes/${sourceModeId}`;
            const legacySnap = await getDoc(doc(db, legacyPath));
            if (legacySnap.exists()) {
              refsToDelete.add(legacyPath);
            }
          }
        }
      }

      const batch = writeBatch(db);
      for (const path of refsToDelete) {
        batch.delete(doc(db, path));
      }
      await batch.commit();

      [...refsToDelete].forEach((path) => {
        clearPlayerStatsSeasonCache(teamId, getSeasonIdFromPath(path) || item?.seasonId);
      });

      setRecentSubmissions((prev) => ({
        team: (prev.team || []).filter((row) => !refsToDelete.has(row.deleteRefPath)),
        enemy: (prev.enemy || []).filter((row) => !refsToDelete.has(row.deleteRefPath)),
        player: (prev.player || []).filter((row) => !refsToDelete.has(row.deleteRefPath)),
      }));
      return { ok: true };
    } catch (error) {
      console.error('Failed to delete recent submission:', error);
      return { ok: false, error };
    }
  };

  return {
    recentLoading,
    recentSubmissions,
    availableSeasons,
    deleteRecentSubmission,
  };
};
